/**
 * useSchema hook - schema CRUD operations.
 *
 * @package Schema_AI
 */

import { useState, useEffect, useCallback } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';

export function useSchema( postId ) {
	const [ schemas, setSchemas ] = useState( [] );
	const [ loading, setLoading ] = useState( false );
	const [ error, setError ] = useState( null );
	const [ stats, setStats ] = useState( null );

	const fetchSchemas = useCallback( async ( id ) => {
		const targetId = id || postId;
		if ( ! targetId ) {
			return [];
		}
		setLoading( true );
		setError( null );
		try {
			const data = await apiFetch( { path: `/ai-schema-gen/v1/schema/${ targetId }` } );
			// API returns { schemas: [...], total: N } — extract the array.
			const list = Array.isArray( data )
				? data
				: Array.isArray( data?.schemas )
					? data.schemas
					: [];
			setSchemas( list );
			return list;
		} catch ( err ) {
			if ( err.code !== 'schema_not_found' ) {
				setError( err.message );
			}
			setSchemas( [] );
			return [];
		} finally {
			setLoading( false );
		}
	}, [ postId ] );

	const saveSchema = useCallback( async ( data ) => {
		setLoading( true );
		try {
			const result = await apiFetch( {
				path: '/ai-schema-gen/v1/schema',
				method: 'POST',
				data,
			} );
			if ( postId ) {
				await fetchSchemas( postId );
			}
			return result;
		} catch ( err ) {
			setError( err.message );
			throw err;
		} finally {
			setLoading( false );
		}
	}, [ postId, fetchSchemas ] );

	const updateSchema = useCallback( async ( schemaId, schemaData ) => {
		setLoading( true );
		try {
			const result = await apiFetch( {
				path: `/ai-schema-gen/v1/schema/${ schemaId }`,
				method: 'PUT',
				data: { schema_data: schemaData },
			} );
			await fetchSchemas( postId );
			return result;
		} catch ( err ) {
			setError( err.message );
			throw err;
		} finally {
			setLoading( false );
		}
	}, [ postId, fetchSchemas ] );

	const deleteSchema = useCallback( async ( schemaId ) => {
		setLoading( true );
		try {
			await apiFetch( {
				path: `/ai-schema-gen/v1/schema/${ schemaId }`,
				method: 'DELETE',
			} );
			await fetchSchemas( postId );
		} catch ( err ) {
			setError( err.message );
			throw err;
		} finally {
			setLoading( false );
		}
	}, [ postId, fetchSchemas ] );

	const fetchStats = useCallback( async () => {
		try {
			const data = await apiFetch( { path: '/schema-ai/v1/stats' } );
			setStats( data );
			return data;
		} catch ( err ) {
			return null;
		}
	}, [] );

	useEffect( () => {
		if ( postId ) {
			fetchSchemas( postId );
		}
	}, [ postId, fetchSchemas ] );

	return { schemas, loading, error, stats, fetchSchemas, saveSchema, updateSchema, deleteSchema, fetchStats };
}

export default useSchema;
